// src/components/UserFormModal.jsx
import React, { useState, useEffect } from 'react';
import './UserFormModal.css';

const UserFormModal = ({ user, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    plan_days: '',
    password: '',
    is_active: 0,
    logo: '',
    website_url: '',
    company_name: '',
    address: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile || '',
        plan_days: user.plan_days || '',
        is_active: user.is_active ?? 0,
        logo: user.logo || '',
        website_url: user.website_url || '',
        company_name: user.company_name || '',
        address: user.address || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile is required';
    }

    if (!formData.plan_days) {
      newErrors.plan_days = 'Plan duration is required';
    } else if (formData.plan_days < 1) {
      newErrors.plan_days = 'Plan must be at least 1 day';
    }

    if (!user && !formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!user && formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await onSubmit(formData);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save user' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container p-4" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className={`bi ${user ? 'bi-pencil-square' : 'bi-person-plus'}`}></i>
            {user ? 'Edit User' : 'Create New User'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-grid">
            {/* Basic Info */}
            <div className="form-group">
              <label htmlFor="name">Full Name <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="Enter full name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address <span className="required">*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="user@example.com"
                disabled={!!user}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile Number <span className="required">*</span></label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className={errors.mobile ? 'error' : ''}
                placeholder="Mobile number"
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="plan_days">Plan Duration (Days) <span className="required">*</span></label>
              <input
                type="number"
                id="plan_days"
                name="plan_days"
                value={formData.plan_days}
                onChange={handleChange}
                className={errors.plan_days ? 'error' : ''}
                placeholder="30"
                min="1"
              />
              {errors.plan_days && <span className="error-message">{errors.plan_days}</span>}
            </div>

            {/* Company Info */}
            <div className="form-group">
              <label htmlFor="company_name">Company Name</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Company or firm name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="website_url">Website URL</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="logo">Logo URL</label>
              <input
                type="text"
                id="logo"
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Full address"
              />
            </div>

            {/* Password - new user only */}
            {!user && (
              <div className="form-group full-width">
                <label htmlFor="password">Password <span className="required">*</span></label>
                <div className="password-input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Enter password (min 8 characters)"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
            )}

            {/* Status - edit only */}
            {user && (
              <div className="form-group">
                <label htmlFor="is_active">Status</label>
                <select
                  id="is_active"
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleChange}
                  className="status-select"
                >
                  <option value={0}>Active</option>
                  <option value={1}>Suspended</option>
                  <option value={2}>Expired</option>
                </select>
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="submit-error">
              <i className="bi bi-exclamation-circle"></i>
              {errors.submit}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className={`bi ${user ? 'bi-check-circle' : 'bi-plus-circle'}`}></i>
                  {user ? 'Update User' : 'Create User'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
